import React, { createContext, useState } from 'react';
import useLogin from '../hooks/queries/auth/useLogin';
import useUserProfile from '../hooks/queries/auth/useUserProfile';

import { useDispatch } from 'react-redux';
import { appActions } from '../store/modules/app';

import { keys } from '../lib/storage';
import { AuthRequest, IAuthContext, UserProfileResponse } from '../types/common/AuthType';
import { PermissionAction, PermissionSubjects } from '../types/common/PermissionType';

export const AuthContext = createContext<IAuthContext>({
  isLoggedIn: false,
  userLoading: true,
  user: null,
  userPermissions: {},
  login: async () => {
    return;
  },
  logout: () => {
    return;
  },
  can: () => false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    const token = localStorage.getItem(keys.user);
    return !!token;
  });

  const loginMutation = useLogin();

  const { isLoading, data: user } = useUserProfile({
    options: {
      enabled: isLoggedIn,
      onSuccess: (response: UserProfileResponse) => {
        // 기타 많은 페이지에서 redux store를 사용하고 있으므로 해당 라인을 사용해 store를 업데이트
        // todo: 리덕스 스토어를 참고하는 컴포넌트를 컨텍스트의 user를 참고하도록 수정
        dispatch(appActions.fetchUserData({ adminInfo: response }));
      },
    },
  });

  const login = async (payload: AuthRequest) => {
    try {
      const response = await loginMutation.mutateAsync(payload);
      const {
        data: { token },
      } = response;
      if (token) {
        localStorage.setItem(keys.user, token);
      }
      setIsLoggedIn(true);
    } catch (err) {
      console.error(err);
    }
  };

  const logout = () => {
    localStorage.removeItem(keys.user);
    setIsLoggedIn(false);
  };

  const can = (action: PermissionAction, subject: PermissionSubjects): boolean => {
    const userPermissions = user?.permission;
    return userPermissions?.[subject]?.[action] === true;
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        login,
        logout,
        userLoading: isLoading,
        user,
        userPermissions: user?.permission,
        can,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
